// src/pages/EnterpriseUserData.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Download, Eye, Folder, AlertCircle } from 'lucide-react';
import Navbar4 from '../components/Navbar4';
import { getEnterpriseUserCompleteData, downloadEnterpriseViewVersion } from '../services/EnterpriseAdminAccess';

const EnterpriseUserData = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get state from navigation - ADDED scrollPosition for preservation
  const { enterpriseId, userId, userEmail, scrollPosition } = location.state || {};
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [selectedVersions, setSelectedVersions] = useState({});
  const [activeDropdowns, setActiveDropdowns] = useState(new Set());

  // ADDED: Store scroll position before navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('enterpriseTabScrollPosition', window.pageYOffset.toString());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!enterpriseId || !userId) {
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getEnterpriseUserCompleteData(enterpriseId, userId);
        
        if (response.status === 'success') {
          setUserData(response.data);
          
          // Initialize selected versions to the latest version for each view
          const initialVersions = {};
          response.data.projects.forEach((project, projectIndex) => {
            project.views.forEach((view, viewIndex) => {
              if (view.versions && view.versions.length > 0) {
                const latestVersion = view.versions[0]; // Versions are sorted newest first
                initialVersions[`${projectIndex}-${viewIndex}`] = latestVersion.versionId;
              }
            });
          });
          setSelectedVersions(initialVersions);
        } else {
          setError('Failed to load user data');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [enterpriseId, userId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdowns(new Set());
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Handle project accordion toggle
  const toggleProject = (projectIndex) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectIndex)) {
      newExpanded.delete(projectIndex);
    } else {
      newExpanded.add(projectIndex);
    }
    setExpandedProjects(newExpanded);
  };

  // Handle version dropdown toggle
  const toggleVersionDropdown = (e, viewKey) => {
    e.stopPropagation();
    const newActiveDropdowns = new Set(activeDropdowns);
    if (newActiveDropdowns.has(viewKey)) {
      newActiveDropdowns.delete(viewKey);
    } else {
      // Close all others and open this one
      newActiveDropdowns.clear();
      newActiveDropdowns.add(viewKey);
    }
    setActiveDropdowns(newActiveDropdowns);
  };

  // Handle version selection
  const selectVersion = (viewKey, versionId, versionNumber) => {
    setSelectedVersions(prev => ({
      ...prev,
      [viewKey]: versionId
    }));
    setActiveDropdowns(new Set());
  };

  // Handle download action
  const handleDownload = async (projectIndex, viewIndex) => {
    const viewKey = `${projectIndex}-${viewIndex}`;
    const versionId = selectedVersions[viewKey];
    const view = userData.projects[projectIndex].views[viewIndex];
    
    if (!versionId) return;

    try {
      await downloadEnterpriseViewVersion(enterpriseId, view.viewId, versionId);
    } catch (err) {
      console.error('Error downloading version:', err);
      setError('Failed to download file. Please try again.');
    }
  };

  // Handle view action - navigate to EnterpriseViewData page
  const handleView = async (projectIndex, viewIndex) => {
    const viewKey = `${projectIndex}-${viewIndex}`;
    const versionId = selectedVersions[viewKey];
    const view = userData.projects[projectIndex].views[viewIndex];
    
    if (!versionId) return;

    try {
      // Navigate to the EnterpriseViewData page with all necessary data
      navigate('/enterprise-view-data', {
        state: {
          enterpriseId,
          viewId: view.viewId,
          versionId,
          viewTitle: view.viewTitle,
          projectTitle: userData.projects[projectIndex].projectTitle,
          userEmail: userData.userEmail || userEmail,
          enterpriseName: userData.enterpriseName
        }
      });
    } catch (err) {
      console.error('Error navigating to view:', err);
      setError('Failed to view content. Please try again.');
    }
  };

  // Get version number for display
  const getVersionNumber = (projectIndex, viewIndex, versionId) => {
    const view = userData?.projects[projectIndex]?.views[viewIndex];
    if (!view || !view.versions) return '';
    
    const versionIndex = view.versions.findIndex(v => v.versionId === versionId);
    return versionIndex !== -1 ? view.versions.length - versionIndex : '';
  };

  // MODIFIED: Handle back navigation with preserved state
  const handleBack = () => {
    // Get the stored scroll position
    const savedScrollPosition = sessionStorage.getItem('enterpriseTabScrollPosition') || scrollPosition || 0;
    
    navigate('/account', {
      state: {
        activeTab: 'enterprise',
        scrollPosition: parseInt(savedScrollPosition, 10),
        preserveState: true
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar4 title="Enterprise User Data" onBack={handleBack} />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar4 title="Enterprise User Data" onBack={handleBack} />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4">
            <div className="flex items-center text-red-500 mb-4">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>Error</span>
            </div>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={handleBack}
              className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar4 title="Enterprise User Data" onBack={handleBack} />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Folder className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No data available for this user</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar4 title="Enterprise User Data" onBack={handleBack} />
      
      <div className="flex flex-col items-center min-h-screen bg-gray-50">
        <div className="w-full max-w-2xl mx-auto bg-white shadow rounded-lg mt-6 p-8">
          {/* Enterprise Info */}
          <div className="mb-8">
            <div className="text-base font-semibold text-gray-900 tracking-wide mb-1">
              {userData.enterpriseName || 'ENTERPRISE NAME'}
            </div>
            <div className="text-gray-400 text-sm font-medium">
              {userData.userEmail || userEmail || 'user@example.com'}
            </div>
          </div>

          {/* Projects List */}
          <div id="projects-list">
            {userData.projects.length === 0 ? (
              <div className="text-center py-12">
                <div className="flex justify-center mb-4">
                  <Folder className="text-4xl text-gray-400" size={48} />
                </div>
                <div className="text-gray-900 font-semibold text-lg mb-1">
                  No projects or views found for the user
                </div>
                <div className="text-gray-400 text-sm">
                  Please check back later or contact your administrator.
                </div>
              </div>
            ) : (
              userData.projects.map((project, projectIndex) => (
                <div key={project.projectId} className="mb-3">
                  {/* Project Header */}
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-5 py-4 rounded-lg bg-gray-100 hover:bg-gray-200 transition group focus:outline-none"
                    onClick={() => toggleProject(projectIndex)}
                  >
                    <span className="text-gray-900 font-medium">{project.projectTitle}</span>
                    <ChevronDown 
                      className={`text-gray-400 text-xl transition-transform group-hover:text-gray-900 ${
                        expandedProjects.has(projectIndex) ? 'rotate-180' : ''
                      }`}
                      size={20}
                    />
                  </button>

                  {/* Project Content */}
                  {expandedProjects.has(projectIndex) && (
                    <div className="bg-white border border-t-0 border-gray-200 rounded-b-lg px-5 pb-4 pt-2">
                      <div className="space-y-4">
                        {project.views.map((view, viewIndex) => {
                          const viewKey = `${projectIndex}-${viewIndex}`;
                          const selectedVersionId = selectedVersions[viewKey];
                          const selectedVersionNumber = getVersionNumber(projectIndex, viewIndex, selectedVersionId);

                          return (
                            <div key={view.viewId} className="bg-gray-50 rounded-lg px-4 py-4 flex flex-col shadow-sm">
                              <div className="text-gray-900 font-medium mb-2">{view.viewTitle}</div>
                              <div className="flex items-center justify-between">
                                {/* Version Dropdown */}
                                <div className="relative">
                                  <button
                                    type="button"
                                    className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-gray-900 font-medium focus:outline-none hover:border-gray-400 transition min-w-[120px]"
                                    onClick={(e) => toggleVersionDropdown(e, viewKey)}
                                  >
                                    <span className="mr-2">Version {selectedVersionNumber}</span>
                                    <ChevronDown className="text-gray-400 text-lg" size={16} />
                                  </button>
                                  
                                  {activeDropdowns.has(viewKey) && (
                                    <ul className="absolute left-0 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-[120px]">
                                      {view.versions.map((version, versionIndex) => {
                                        const versionNumber = view.versions.length - versionIndex;
                                        return (
                                          <li
                                            key={version.versionId}
                                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                            onClick={() => selectVersion(viewKey, version.versionId, versionNumber)}
                                          >
                                            Version {versionNumber}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center space-x-3 ml-4">
                                  <div className="relative group">
                                    <button
                                      type="button"
                                      className="p-2 rounded-full hover:bg-gray-200 transition"
                                      onClick={() => handleDownload(projectIndex, viewIndex)}
                                      title="Download"
                                    >
                                      <Download className="text-gray-900" size={20} />
                                    </button>
                                  </div>
                                  <div className="relative group">
                                    <button
                                      type="button"
                                      className="p-2 rounded-full hover:bg-gray-200 transition"
                                      onClick={() => handleView(projectIndex, viewIndex)}
                                      title="View"
                                    >
                                      <Eye className="text-gray-900" size={20} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseUserData;